import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, Users, Building2, DollarSign, CheckCircle2, AlertTriangle, UserX, Eye, TrendingUp, Scale,
} from "lucide-react";
import { sr } from "../../lib/sr";
import HealthBadge from "./HealthBadge";

const money = (n) => "$" + Number(n || 0).toLocaleString();

function ClientRow({ c, staff, saving, onReassign, onView }) {
  return (
    <div className="sr-card" data-testid={`workload-client-${c.id}`}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", flexWrap: "wrap" }}>
      <span style={{ flex: 1, minWidth: 140, color: "var(--navy)", fontWeight: 600, fontSize: 14 }}>{c.name}</span>
      <HealthBadge health={c.health} size="sm" />
      <span className="sr-muted" style={{ fontSize: 12 }}>{c.live_campaigns} live · {money(c.monthly_budget)}/mo</span>
      <select className="sr-select" value={c.__mgr || ""} disabled={saving === c.id}
        onChange={(e) => onReassign(c.id, e.target.value)} data-testid={`reassign-select-${c.id}`}
        style={{ padding: "4px 8px", fontSize: 12, maxWidth: 180 }}>
        <option value="">— Unassigned —</option>
        {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
      </select>
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => onView(c.id)} data-testid={`workload-view-${c.id}`}>
        <Eye size={13} />
      </button>
    </div>
  );
}

export default function AdminWorkload() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const [rebalancing, setRebalancing] = useState(false);

  const load = () => sr.adminManagersWorkload().then(setData).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  const rebalance = async () => {
    if (!window.confirm("Redistribute every client evenly across all account managers? This overwrites current assignments.")) return;
    setRebalancing(true);
    try { const r = await sr.adminRebalanceBook(); toast.success(`Rebalanced ${r.clients} clients across ${r.managers} managers (~${r.per_manager} each)`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not rebalance"); }
    finally { setRebalancing(false); }
  };

  const reassign = async (clientId, managerId) => {
    setSaving(clientId);
    try { await sr.adminAssignManager(clientId, managerId); toast.success(managerId ? "Reassigned" : "Unassigned"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reassign"); }
    finally { setSaving(null); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load workload. Admin access is required.</p></div>;

  const staff = data.staff || [];
  const view = (id) => nav(`/app/admin/clients/${id}`);

  return (
    <div data-testid="admin-workload-page">
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <span className="sr-badge" style={{ marginBottom: 8 }}><ShieldCheck size={12} /> Admin · Workload</span>
          <h1 style={{ fontSize: 30 }}>Manager leaderboard</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Live campaigns, spend and results per manager — so you can see who needs backup. Reassign a client from any row, or rebalance the whole book in one click.</p>
        </div>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={rebalance} disabled={rebalancing} data-testid="rebalance-book-btn">
          {rebalancing ? <Loader2 size={14} className="sr-spin" /> : <Scale size={14} />} Rebalance book
        </button>
      </div>

      {data.unassigned.count > 0 && (
        <div className="sr-card" style={{ marginBottom: 16, border: "1px solid #E3C878", background: "#FCF8EE" }} data-testid="workload-unassigned">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <UserX size={16} style={{ color: "#9A7412" }} />
            <strong style={{ color: "#7A5B12" }}>Unassigned clients</strong>
            <span className="sr-badge sr-badge-amber">{data.unassigned.count}</span>
            {data.unassigned.assisted > 0 && <span className="sr-badge sr-badge-gray">{data.unassigned.assisted} assisted</span>}
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {data.unassigned.clients.map((c) => <ClientRow key={c.id} c={c} staff={staff} saving={saving} onReassign={reassign} onView={view} />)}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {data.managers.map((m) => (
          <div key={m.id} className="sr-card" data-testid={`manager-${m.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Users size={16} style={{ color: "var(--blue)" }} />
                <strong style={{ color: "var(--navy)", fontSize: 16 }}>{m.name || m.email}</strong>
                <span className="sr-badge sr-badge-gray">{m.role.replace(/_/g, " ")}</span>
              </div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }} data-testid={`manager-stats-${m.id}`}>
                <span className="sr-muted" style={{ fontSize: 13 }}><Building2 size={12} /> {m.total_clients} clients</span>
                <span className="sr-muted" style={{ fontSize: 13 }}><CheckCircle2 size={12} /> {m.live_campaigns} live</span>
                <span className="sr-muted" style={{ fontSize: 13 }}><DollarSign size={12} /> {money(m.spend)} spend</span>
                <span className="sr-muted" style={{ fontSize: 13 }}><TrendingUp size={12} /> {Number(m.results || 0).toLocaleString()} results</span>
                {m.attention > 0 && <span className="sr-badge sr-badge-amber"><AlertTriangle size={11} /> {m.attention} need attention</span>}
              </div>
            </div>
            {m.clients.length === 0 ? (
              <p className="sr-muted" style={{ fontSize: 13, marginTop: 10 }}>No clients assigned yet.</p>
            ) : (
              <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                {m.clients.map((c) => <ClientRow key={c.id} c={{ ...c, __mgr: m.id }} staff={staff} saving={saving} onReassign={reassign} onView={view} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

